import { useEffect, useState, type FC } from 'react'

import { LoaderCircle } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import { twMerge } from 'tailwind-merge'

import queries from '@/api/queries'
import { ecomPolicyResources } from '@/resources/ecom-policy'
import { isEcomMigrationOrder, isEcomOrderOpen } from '@/util/misc'

import type { CoreGreenpoolOrder } from '../types'

import { isCoreOrder, mergeAndSortOrders } from './helpers'
import { OrderListItem } from './OrderListItem'

type OrderListProps = {
  isOnlyOpenOrdersVisible?: boolean
  className?: string
}

export const OrderList: FC<OrderListProps> = ({ isOnlyOpenOrdersVisible = false, className }) => {
  const { data, isLoading } = useQuery(queries.policyList(), ecomPolicyResources.getList)

  const [maxNumberOfOrdersToShow, setMaxNumberOfOrdersToShow] = useState(6)
  useEffect(() => {
    const updateMaxOrders = () => {
      const width = window.innerWidth
      if (isOnlyOpenOrdersVisible && width < 768) {
        const maxNumber = width >= 600 ? 2 : 1
        setMaxNumberOfOrdersToShow(maxNumber)
      } else {
        setMaxNumberOfOrdersToShow(6)
      }
    }
    // Set initial value
    updateMaxOrders()

    // Add event listener for resize
    window.addEventListener('resize', updateMaxOrders)

    // Cleanup listener
    return () => {
      window.removeEventListener('resize', updateMaxOrders)
    }
  }, [isOnlyOpenOrdersVisible])

  // Early return for loading state
  if (isLoading) {
    return (
      <div className={twMerge('m-4', className)}>
        <div className="grid-cols-fill-15 m-0 grid gap-4 p-0">
          <LoaderCircle />
        </div>
      </div>
    )
  }

  // Extract data
  const { orders: coreOrders = [], greenpoolOrders = [] } = data || {}

  // Merge and prepare orders
  const allOrders: CoreGreenpoolOrder[] = mergeAndSortOrders(coreOrders, greenpoolOrders)

  // Filter orders if needed; show only first 6 orders at the Shop page
  let ordersToDisplay = []
  if (isOnlyOpenOrdersVisible) {
    const openOrders = allOrders.filter(
      order => isCoreOrder(order) && isEcomOrderOpen(order.status)
    )
    ordersToDisplay =
      openOrders.length > maxNumberOfOrdersToShow
        ? openOrders.slice(0, maxNumberOfOrdersToShow)
        : openOrders
  } else {
    ordersToDisplay = allOrders.filter(
      order => (isCoreOrder(order) && !isEcomMigrationOrder(order.status)) || !isCoreOrder(order)
    )
  }

  return (
    <div className={twMerge('my-4', className)}>
      <div className="grid-cols-fill-15 m-0 grid gap-4 p-0">
        {ordersToDisplay.map((order: CoreGreenpoolOrder) => (
          <OrderListItem key={order.id} {...order} />
        ))}
      </div>
    </div>
  )
}
